import React, { useState, useCallback, useEffect, useMemo } from "react";
import { makeStyles, Slider, Typography, Button, InputBase } from "@material-ui/core";

import useFilters from "hooks/useFilters";
import useDebounce from "hooks/useDebounce";

export default function PriceSlider({ min, max, close }) {
  const classes = useStyle();
  const [{ filters: selectedFilters }, dispatch] = useFilters();

  const flooredMin = Math.floor(min / 100);
  const flooredMax = Math.floor(max / 100);

  const priceMin = useMemo(
    () => (Array.isArray(selectedFilters.price_min) ? selectedFilters.price_min[0] / 100 : flooredMin),
    [selectedFilters, flooredMin]
  );
  const priceMax = useMemo(
    () => (Array.isArray(selectedFilters.price_max) ? selectedFilters.price_max[0] / 100 : flooredMax),
    [selectedFilters, flooredMax]
  );

  const [value, setValue] = useState([0, 0]);

  const reset = useCallback(() => {
    dispatch({ type: "resetMultiple", filters: ["price_min", "price_max"] });
  }, [dispatch]);

  const debouncedValue = useDebounce(value, 100);

  useEffect(() => {
    if (!debouncedValue[0] || !debouncedValue[1]) {
      return;
    }
    if (debouncedValue[0] !== priceMin || debouncedValue[1] !== priceMax) {
      dispatch({
        type: "setMultiple",
        filters: {
          price_min: `${debouncedValue[0] || 0}00`,
          price_max: `${debouncedValue[1] || 0}00`,
        },
      });
    }
  }, [debouncedValue, dispatch, priceMin, priceMax]);

  return (
    <div className={classes.container}>
      <Slider
        color="secondary"
        min={flooredMin}
        max={flooredMax}
        value={[priceMin || 0, priceMax || 0]}
        onChange={(ev, value) => setValue(value)}
        className={classes.slider}
      />
      <div className={classes.inline}>
        <div className={classes.info}>
          <Typography variant="caption" className={classes.caption}>
            prix minimum
          </Typography>
          <div className={classes.priceContainer}>
            <InputBase
              value={priceMin || ""}
              type="number"
              onChange={(ev) => setValue([parseInt(ev.target.value), value[1]])}
            />
            <Typography>€</Typography>
          </div>
        </div>
        <div className={classes.separator}>-</div>
        <div className={classes.info}>
          <Typography variant="caption" className={classes.caption}>
            prix maximum
          </Typography>
          <div className={classes.priceContainer}>
            <InputBase
              value={priceMax || ""}
              type="number"
              onChange={(ev) => setValue([value[0], parseInt(ev.target.value)])}
            />
            <Typography>€</Typography>
          </div>
        </div>
      </div>
      {close && (
        <div className={classes.buttonContainer}>
          <Button
            disableRipple
            variant="contained"
            fullWidth
            onClick={() => {
              reset();
              close();
            }}
            className={classes.reinit}
          >
            Réinitialiser
          </Button>
          <Button variant="contained" color="primary" onClick={close} fullWidth>
            Appliquer
          </Button>
        </div>
      )}
    </div>
  );
}

const useStyle = makeStyles((theme) => ({
  info: {
    flex: "1 1 auto",
  },
  caption: {
    fontSize: 16,
  },
  slider: {
    marginTop: theme.spacing(4),
    width: "calc(100% - 20px)",
    "& .MuiSlider-rail": {
      backgroundColor: "#b7b6b6",
      color: "#b7b6b6",
    },
    "& .MuiSlider-track": {
      backgroundColor: "white",
      borderTop: "1px solid #b7b6b6",
      height: 5,
      marginTop: -2,
      borderBottom: "1px solid #b7b6b6",
    },
    "& .MuiSlider-thumb": {
      width: 30,
      height: 30,
      marginTop: -13,
    },
  },
  separator: {
    marginTop: 20,
    marginLeft: 20,
    marginRight: 20,
  },
  reinit: {
    backgroundColor: theme.palette.grey[200],
    fontWeight: 500,
    fontSize: 18,
    marginBottom: theme.spacing(2),
    boxShadow: "none",
    "&:hover": {
      boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
      backgroundColor: theme.palette.grey[200],
    },
  },
  buttonContainer: {
    width: "100%",
    marginTop: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
  },
  container: {
    minWidth: 300,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    rowGap: "20px",
  },
  inline: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  priceContainer: {
    flex: "1 1 auto",
    borderRadius: 5,
    border: "1px solid #D7D7D7",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 10,
  },
}));
